import React, { useEffect, useState } from "react";
import { Flame, Target, Award, CalendarCheck } from "lucide-react";
import { api } from "@/lib/api";

function Cell({ icon: Icon, label, value, sub, testid, hot }) {
  return (
    <div data-testid={testid} className="bg-black/25 border border-white/5 rounded-lg px-4 py-3">
      <div className="label-caps flex items-center gap-1.5 text-parchment/50"><Icon className={`w-3 h-3 ${hot ? "text-oracle" : ""}`} strokeWidth={1.5} /> {label}</div>
      <div className={`font-heading text-3xl mt-1 tabular-nums ${hot ? "text-oracle" : "text-parchment"}`}>{value}</div>
      {sub && <div className="font-body text-[11px] text-parchment/40 mt-0.5">{sub}</div>}
    </div>
  );
}

export function StreakStats({ refreshKey }) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    api.get("/picks/stats").then((r) => setSt(r.data)).catch(() => setSt(null));
  }, [refreshKey]);

  if (!st || st.picks === 0) return null;

  return (
    <div data-testid="streak-stats" className="bg-surface border border-white/5 rounded-xl p-5 sm:p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="label-caps text-oracle">Your track record</div>
          <h3 className="font-heading text-2xl text-parchment mt-1">
            {st.draws_tracked === 0
              ? <>Your picks meet their first draw <span className="italic text-oracle">soon.</span></>
              : st.three_plus > 0
                ? <>You've hit 3+ numbers <span className="italic text-oracle">{st.three_plus} time{st.three_plus === 1 ? "" : "s"}.</span></>
                : <>Every draw sharpens the <span className="italic text-oracle">Oracle.</span></>}
          </h3>
          <p className="font-body text-xs text-parchment/50 mt-1">Saved picks are checked against every drawing after the day you saved them.</p>
        </div>
        {st.timeline.length > 0 && (
          <div data-testid="streak-timeline" className="flex items-end gap-1.5">
            {st.timeline.map((t) => (
              <div key={t.date} className="flex flex-col items-center gap-1" title={`${t.date}: best ${t.best} match${t.best === 1 ? "" : "es"}${t.won ? " · prize" : ""}`}>
                <div className={`w-4 rounded-sm ${t.won ? "bg-oracle" : t.best >= 3 ? "bg-oracle/70" : t.best >= 1 ? "bg-parchment/40" : "bg-white/10"}`} style={{ height: `${8 + t.best * 9}px` }} />
                <span className="font-body text-[9px] text-parchment/35">{t.date.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Cell icon={CalendarCheck} label="Draws tracked" value={st.draws_tracked} sub={`${st.pick_draw_pairs} pick checks`} testid="streak-draws" />
        <Cell icon={Target} label="3+ number hits" value={st.three_plus} sub={`${st.three_plus_rate}% of checks`} testid="streak-three-plus" hot={st.three_plus > 0} />
        <Cell icon={Award} label="Prizes hit" value={st.prizes} sub={st.prize_usd ? `≈ $${st.prize_usd.toLocaleString()}` : "best match: " + st.best_match} testid="streak-prizes" hot={st.prizes > 0} />
        <Cell icon={Flame} label="Current streak" value={st.streak} sub="draws in a row with 2+ matches" testid="streak-current" hot={st.streak > 0} />
      </div>
    </div>
  );
}
